import { formatCurrency, formatDate, formatDateTime, formatNumber } from "@/utils/format";
import {
  labelPedidoSiengeStatus,
  type PurchaseOrderSummary,
} from "@/services/suprimentos";

export interface PurchaseOrderPdfItemRow {
  itemNumber: string;
  code: string;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  total: string;
}

export interface PurchaseOrderPdfModel {
  orderNumber: string;
  supplier: string;
  date: string;
  status: string;
  costCenter: string;
  value: string;
  observations: string;
  items: PurchaseOrderPdfItemRow[];
  itemsTotal: string;
  emitidoEm: string;
  geradoPor: string;
  filenameBase: string;
  billId?: string;
  creditorFallback?: string;
}

function sanitizeFilename(name: string): string {
  return name
    .replace(/[^\w\s.-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80) || "pedido-compra";
}

export function buildPurchaseOrderPdfModel(
  summary: PurchaseOrderSummary,
  options: { geradoPor: string; billId?: string; creditorFallback?: string } = {
    geradoPor: "Usuário",
  },
): PurchaseOrderPdfModel {
  const orderNumber = String(summary.orderNumber ?? summary.purchaseOrderId).trim() || "—";
  const itemsTotalNum = summary.items.reduce((s, it) => s + (it.totalAmount ?? 0), 0);
  const headerValue = summary.value > 0 ? summary.value : itemsTotalNum;
  const supplier =
    summary.supplier.trim() ||
    options.creditorFallback?.trim() ||
    "—";

  return {
    orderNumber,
    supplier,
    date: summary.date ? formatDate(summary.date) : "—",
    status: labelPedidoSiengeStatus(summary.status),
    costCenter: summary.costCenterName?.trim() || summary.costCenterId?.trim() || "—",
    value: formatCurrency(headerValue),
    observations: summary.observations.trim() || "—",
    items: summary.items.map((it) => ({
      itemNumber: it.itemNumber || "—",
      code: it.code || "—",
      description: it.description || "—",
      quantity: formatNumber(it.quantity, 2),
      unit: it.unit || "—",
      unitPrice: formatCurrency(it.netPrice || it.unitPrice || 0),
      total: formatCurrency(it.totalAmount || 0),
    })),
    itemsTotal: formatCurrency(itemsTotalNum || headerValue),
    emitidoEm: formatDateTime(new Date().toISOString()),
    geradoPor: options.geradoPor.trim() || "Usuário",
    filenameBase: sanitizeFilename(`pedido-compra-${orderNumber}`),
    billId: options.billId,
    creditorFallback: options.creditorFallback,
  };
}
